import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import './FinalCTA.css';

const FinalCTA = () => {
  const [ref, isVisible] = useScrollReveal(0.12);
  const [errors, setErrors] = React.useState({});

  const validators = {
    user_name: (v) => {
      if (!v.trim()) return 'Name is required';
      if (!/^[A-Za-z\s]{2,50}$/.test(v.trim())) return 'Name should contain letters only';
      return '';
    },
    user_email: (v) => {
      if (!v.trim()) return 'Email is required';
      // Standard email format: text@text.text (no spaces, single @, valid domain)
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v.trim())) return 'Please enter a valid email address (e.g. name@company.com)';
      return '';
    },
    user_phone: (v) => {
      if (!v.trim()) return ''; // optional field
      if (!/^[0-9]{10}$/.test(v.trim())) return 'Enter a valid 10-digit phone number';
      return '';
    },
  };

  const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];

  // Block any keystroke that isn't a letter or space
  const handleNameKeyDown = (e) => {
    if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (!/^[A-Za-z\s]$/.test(e.key)) {
      e.preventDefault();
      setErrors((prev) => ({ ...prev, user_name: 'Only letters and spaces are allowed' }));
    }
  };

  const handleNameInput = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^A-Za-z\s]/g, '').slice(0, 50);
    e.target.value = cleaned;

    if (raw.replace(/[^A-Za-z\s]/g, '').length !== raw.length) {
      setErrors((prev) => ({ ...prev, user_name: 'Only letters and spaces are allowed' }));
    } else {
      setErrors((prev) => ({ ...prev, user_name: validators.user_name(cleaned) }));
    }
  };

  // Block any keystroke that isn't a digit
  const handlePhoneKeyDown = (e) => {
    if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      setErrors((prev) => ({ ...prev, user_phone: 'Only numbers are allowed' }));
    }
  };

  const handlePhoneInput = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/\D/g, '').slice(0, 10);
    e.target.value = cleaned;

    if (raw.replace(/\D/g, '').length !== raw.length) {
      setErrors((prev) => ({ ...prev, user_phone: 'Only numbers are allowed' }));
    } else {
      setErrors((prev) => ({ ...prev, user_phone: validators.user_phone(cleaned) }));
    }
  };

  // Email can't be keystroke-blocked (needs @ and .), so validate live instead
  const handleEmailInput = (e) => {
    const value = e.target.value;
    setErrors((prev) => ({ ...prev, user_email: validators.user_email(value) }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const errorMsg = validators[name] ? validators[name](value) : '';
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const newErrors = {
      user_name: validators.user_name(form.user_name.value),
      user_email: validators.user_email(form.user_email.value),
      user_phone: validators.user_phone(form.user_phone.value),
    };
    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((msg) => msg);
    if (hasErrors) return;

    try {
      const res = await fetch('https://backend.ambitiousbay-349d1cc1.centralindia.azurecontainerapps.io/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name:     form.user_name.value,
          user_email:    form.user_email.value,
          user_phone:    form.user_phone.value,
          facility_type: form.facility_type.value,
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert('Demo request sent successfully!');
        form.reset();
        setErrors({});
      } else {
        alert('Failed to send. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send. Please try again.');
    }
  };



  return (
    <section className="final-cta" id="demo" ref={ref}>
      {/* Decorative background elements */}
      <div className="cta-bg-grid" />

      <div className="container final-cta__inner">
        <div className={`final-cta__content reveal${isVisible ? ' visible' : ''}`}>
          <span className="final-cta__tag">Get Started Today</span>
          <h2 className="final-cta__headline">
            Transform Your Hospital Operations with
            <br />
            <span className="final-cta__headline-bold">Tecnivorns HMS</span>
          </h2>
          <p className="final-cta__sub">
            From appointments to prescriptions, pharmacy integration to patient records — manage your entire healthcare ecosystem from one powerful platform.
          </p>

            {/* <div className="final-cta__buttons">
            <a href="#schedule" className="cta-btn cta-btn--dark">
              Schedule Demo
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </a>
            <a href="#contact" className="cta-btn cta-btn--outline">
              Contact Sales
            </a>
          </div> */}

          <div className="final-cta__assurances">
            {['Free demo, no credit card', 'Onboarding in under 2 weeks', 'Dedicated support team', 'Data migration included'].map((text) => (
              <div key={text} className="assurance-item">
                <span className="assurance-check">✓</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className={`final-cta__panel reveal reveal-right delay-2${isVisible ? ' visible' : ''}`}>
          <div className="cta-panel">
            <div className="cta-panel__header">
              <span className="panel-badge">Live Demo</span>
              <span className="panel-badge panel-badge--green">Available Now</span>
            </div>
            <h3 className="cta-panel__title">See Tecnivorns in Action</h3>
            <p className="cta-panel__sub">Our team will walk you through a live demo tailored to your hospital type and size.</p>

            <form className="cta-form" onSubmit={handleSubmit}>
              <div className="cta-form__field">
                <input
                  type="text"
                  name="user_name"
                  placeholder="Your Name"
                  className={`cta-input${errors.user_name ? ' cta-input--error' : ''}`}
                  onKeyDown={handleNameKeyDown}
                  onInput={handleNameInput}
                  onBlur={handleBlur}
                  maxLength={50}
                  required
                />
                {errors.user_name && <span className="cta-error">{errors.user_name}</span>}
              </div>
              <div className="cta-form__field">
                <input
                  type="email"
                  name="user_email"
                  placeholder="Work Email"
                  className={`cta-input${errors.user_email ? ' cta-input--error' : ''}`}
                  onInput={handleEmailInput}
                  onBlur={handleBlur}
                  required
                />
                {errors.user_email && <span className="cta-error">{errors.user_email}</span>}
              </div>
              <div className="cta-form__field">
                <input
                  type="tel"
                  name="user_phone"
                  placeholder="Phone Number"
                  className={`cta-input${errors.user_phone ? ' cta-input--error' : ''}`}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  onKeyDown={handlePhoneKeyDown}
                  onInput={handlePhoneInput}
                  onBlur={handleBlur}
                />
                {errors.user_phone && <span className="cta-error">{errors.user_phone}</span>}
              </div>
              <div className="cta-form__field">
                <select name="facility_type" className="cta-input cta-select">                  <option value="">Facility Type</option>
                  <option>Multi-Specialty Hospital</option>
                  <option>Clinic</option>
                  <option>Individual Doctor Practice</option>
                  <option>Hospital Chain</option>
                  <option>Medical Centre</option>
                </select>
              </div>
              <button type="submit" className="cta-submit">
                Request Demo
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </form>

            <p className="cta-panel__note">We typically respond within 2 business hours.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
